import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Descriptions,
  message,
  Typography,
  Card,
  Row,
  Col,
  Divider
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface ContratoAdesaoNOSData {
  id: number;
  lead_id: number;
  user_id: number;
  lead_name: string;
  user_name: string;
  gestor_responsavel: string;
  segmento_nos: string;
  categoria: string;
  tecnologia: string;
  tipologia: string;
  codigo_pacote: string;
  descricao_pacote: string;
  cartoes_portar: string;
  nome_cliente: string;
  nif: string;
  cc_bi_passaporte: string;
  nacionalidade: string;
  morada_instalacao: string;
  codigo_postal_1: string;
  codigo_postal_2: string;
  localidade: string;
  coordenadas: string;
  telefone_contacto: string;
  email: string;
  debito_direto_iban: string;
  nome_titular_debito: string;
  equipamentos_adicionais: string;
  descricao_ofertas: string;
  observacoes: string;
  data_horario_preferencial: string;
  documentos_anexos: string;
  created_at: string;
  updated_at: string;
  status: string;
}

interface ContratoAdesaoNOSReviewModalProps {
  open: boolean;
  onClose: () => void;
}

const ContratoAdesaoNOSReviewModal: React.FC<ContratoAdesaoNOSReviewModalProps> = ({
  open,
  onClose
}) => {
  const [data, setData] = useState<ContratoAdesaoNOSData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ContratoAdesaoNOSData | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/contrato-adesao-nos/review');
      
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
      } else {
        message.error('Erro ao carregar dados');
      }
    } catch (error) {
      message.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);



  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Lead',
      dataIndex: 'lead_name',
      key: 'lead_name',
    },
    {
      title: 'Utilizador',
      dataIndex: 'user_name',
      key: 'user_name',
    },
    {
      title: 'Cliente',
      dataIndex: 'nome_cliente',
      key: 'nome_cliente',
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('pt-PT'),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: any, record: ContratoAdesaoNOSData) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => {
              setSelectedRecord(record);
              setDetailModalOpen(true);
            }}
          >
            Ver
          </Button>
        </Space>
      ),
    },
  ];

  const renderDetailContent = () => {
    if (!selectedRecord) return null;

    return (
      <div>
        <Title level={4}>Detalhes do Contrato de Adesão NOS</Title>
        
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Informações do Gestor e Segmento" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Gestor Responsável">{selectedRecord.gestor_responsavel}</Descriptions.Item>
                <Descriptions.Item label="Segmento NOS">{selectedRecord.segmento_nos}</Descriptions.Item>
                <Descriptions.Item label="Categoria">{selectedRecord.categoria}</Descriptions.Item>
                <Descriptions.Item label="Tecnologia">{selectedRecord.tecnologia}</Descriptions.Item>
                <Descriptions.Item label="Tipologia">{selectedRecord.tipologia}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          
          <Col span={12}>
            <Card title="Detalhes do Pacote" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Código do Pacote">{selectedRecord.codigo_pacote}</Descriptions.Item>
                <Descriptions.Item label="Descrição do Pacote">{selectedRecord.descricao_pacote}</Descriptions.Item>
                <Descriptions.Item label="Cartões a Portar">{selectedRecord.cartoes_portar}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Dados do Cliente" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Nome do Cliente">{selectedRecord.nome_cliente}</Descriptions.Item>
                <Descriptions.Item label="NIF">{selectedRecord.nif}</Descriptions.Item>
                <Descriptions.Item label="CC/BI/Passaporte">{selectedRecord.cc_bi_passaporte}</Descriptions.Item>
                <Descriptions.Item label="Nacionalidade">{selectedRecord.nacionalidade}</Descriptions.Item>
                <Descriptions.Item label="Morada de Instalação">{selectedRecord.morada_instalacao}</Descriptions.Item>
                <Descriptions.Item label="Código Postal">{selectedRecord.codigo_postal_1}-{selectedRecord.codigo_postal_2}</Descriptions.Item>
                <Descriptions.Item label="Localidade">{selectedRecord.localidade}</Descriptions.Item>
                <Descriptions.Item label="Coordenadas">{selectedRecord.coordenadas}</Descriptions.Item>
                <Descriptions.Item label="Telefone">{selectedRecord.telefone_contacto}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedRecord.email}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          
          <Col span={12}>
            <Card title="Pagamento, Equipamentos e Documentos" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="IBAN">{selectedRecord.debito_direto_iban}</Descriptions.Item>
                <Descriptions.Item label="Titular do Débito">{selectedRecord.nome_titular_debito}</Descriptions.Item>
                <Descriptions.Item label="Equipamentos Adicionais">{selectedRecord.equipamentos_adicionais}</Descriptions.Item>
                <Descriptions.Item label="Descrição das Ofertas">{selectedRecord.descricao_ofertas}</Descriptions.Item>
                <Descriptions.Item label="Data/Horário Preferencial">{selectedRecord.data_horario_preferencial}</Descriptions.Item>
                <Descriptions.Item label="Documentos Anexos">
                  {selectedRecord.documentos_anexos ? (
                    <Text type="secondary">
                      {selectedRecord.documentos_anexos} (disponível na secção Documentos)
                    </Text>
                  ) : (
                    <Text type="secondary">Nenhum documento anexado</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        {selectedRecord.observacoes && (
          <>
            <Divider />
            <Card title="Observações" size="small">
              <Text>{selectedRecord.observacoes}</Text>
            </Card>
          </>
        )}

        <Divider />

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text strong>Lead:</Text> {selectedRecord.lead_name}
          </Col>
          <Col span={12}>
            <Text strong>Utilizador:</Text> {selectedRecord.user_name}
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col span={12}>
            <Text strong>Data de Criação:</Text> {new Date(selectedRecord.created_at).toLocaleString('pt-PT')}
          </Col>
        </Row>

      </div>
    );
  };

  return (
    <>
      <Modal
        title="Revisão de Contratos de Adesão NOS"
        open={open}
        onCancel={onClose}
        footer={null}
        width={1200}
      >
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registos`,
          }}
        />
      </Modal>

      <Modal
        title="Detalhes do Contrato de Adesão NOS"
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false);
          setSelectedRecord(null);
        }}
        footer={
          <Button onClick={() => setDetailModalOpen(false)}>
            Fechar
          </Button>
        }
        width={1000}
      >
        {renderDetailContent()}
      </Modal>
    </>
  );
};

export default ContratoAdesaoNOSReviewModal;
